import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, dashboardService } from '../services/auth';
import MerchantHeader from '../components/MerchantHeader';
import { CiScoreBadge } from '../components/CiScoreBadge';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Package, Tag, Target, ArrowUp, ArrowDown } from 'lucide-react';

export default function ConversionInsightsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('skus'); // skus | promotions

  // Mock conversion data by SKU
  const [skuData] = useState([
    { sku: 'SKU-001', title: 'Premium Wireless Headphones', views: 2450, conversions: 186, rate: 7.6, revenue: 9299.14, trend: 12.5 },
    { sku: 'SKU-002', title: 'Organic Skincare Set', views: 1890, conversions: 142, rate: 7.5, revenue: 5679.58, trend: 8.2 },
    { sku: 'SKU-003', title: 'Travel Backpack Pro', views: 1560, conversions: 98, rate: 6.3, revenue: 4899.02, trend: -3.1 },
    { sku: 'SKU-004', title: 'Smart Home Bundle', views: 1230, conversions: 67, rate: 5.4, revenue: 8039.33, trend: 15.8 },
    { sku: 'SKU-005', title: 'Fitness Tracker Elite', views: 980, conversions: 54, rate: 5.5, revenue: 4859.46, trend: 5.2 },
  ]);

  // Mock conversion data by promotion
  const [promotionData] = useState([
    { code: 'SAVE20', type: 'Percentage', uses: 342, conversions: 289, rate: 84.5, revenue: 14449.11, status: 'active' },
    { code: 'SUMMER25', type: 'Percentage', uses: 198, conversions: 156, rate: 78.8, revenue: 7799.44, status: 'active' },
    { code: 'FLASH50', type: 'Fixed', uses: 156, conversions: 134, rate: 85.9, revenue: 6699.66, status: 'expired' },
    { code: 'WELCOME10', type: 'Percentage', uses: 89, conversions: 72, rate: 80.9, revenue: 3599.28, status: 'active' },
  ]);

  // Chart data
  const [conversionTrendData] = useState([
    { date: 'Jan 1', skuConversions: 45, promoConversions: 38 },
    { date: 'Jan 8', skuConversions: 62, promoConversions: 51 },
    { date: 'Jan 15', skuConversions: 78, promoConversions: 65 },
    { date: 'Jan 22', skuConversions: 71, promoConversions: 58 },
    { date: 'Jan 29', skuConversions: 95, promoConversions: 82 },
    { date: 'Feb 5', skuConversions: 88, promoConversions: 74 },
    { date: 'Feb 12', skuConversions: 108, promoConversions: 92 },
  ]);

  const conversionBySource = [
    { name: 'Direct', value: 45, color: '#3EF1C7' },
    { name: 'Email', value: 25, color: '#7A5CFF' },
    { name: 'Social', value: 18, color: '#FFC857' },
    { name: 'Referral', value: 12, color: '#8CF78E' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileData = await authService.getProfile();
      setUser(profileData.member);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading conversion insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <MerchantHeader user={user} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Conversion Insights</h1>
          <p className="text-[#9CB0C7]">Track conversions from SKUs and promotional campaigns</p>
        </div>

        {/* KPI Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Total Conversions</p>
              <Target className="w-5 h-5 text-[#3EF1C7]" />
            </div>
            <p className="text-3xl font-bold text-white">547</p>
            <p className="text-xs text-[#8CF78E] mt-1">+18.5% vs last period</p>
          </div>

          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Avg Conversion Rate</p>
              <TrendingUp className="w-5 h-5 text-[#7A5CFF]" />
            </div>
            <p className="text-3xl font-bold text-white">6.8%</p>
            <p className="text-xs text-[#8CF78E] mt-1">+2.1% improvement</p>
          </div>

          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Promo Revenue</p>
              <Tag className="w-5 h-5 text-[#FFC857]" />
            </div>
            <p className="text-3xl font-bold text-[#8CF78E]">$32,547</p>
            <p className="text-xs text-[#9CB0C7] mt-1">From 785 promo uses</p>
          </div>

          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Top SKU</p>
              <Package className="w-5 h-5 text-[#8CF78E]" />
            </div>
            <p className="text-lg font-bold text-white truncate">SKU-001</p>
            <p className="text-xs text-[#9CB0C7] mt-1">7.6% conversion rate</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Conversion Trend */}
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Conversion Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={conversionTrendData}>
                <defs>
                  <linearGradient id="colorSku" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3EF1C7" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3EF1C7" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorPromo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7A5CFF" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#7A5CFF" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
                <XAxis dataKey="date" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                <YAxis stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                <Tooltip contentStyle={{background: '#0E1426', border: '1px solid #22304A', borderRadius: '8px'}} />
                <Legend />
                <Area type="monotone" dataKey="skuConversions" name="SKU Conversions" stroke="#3EF1C7" fillOpacity={1} fill="url(#colorSku)" />
                <Area type="monotone" dataKey="promoConversions" name="Promo Conversions" stroke="#7A5CFF" fillOpacity={1} fill="url(#colorPromo)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Conversion by Source */}
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Conversion by Source</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={conversionBySource}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {conversionBySource.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{background: '#0E1426', border: '1px solid #22304A', borderRadius: '8px'}} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveView('skus')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeView === 'skus'
                ? 'bg-[#7A5CFF] text-white'
                : 'border border-[#22304A] bg-[#111A2E] text-[#9CB0C7] hover:bg-[#1A2437]'
            }`}
          >
            <Package className="w-4 h-4" />
            By SKU
          </button>
          <button
            onClick={() => setActiveView('promotions')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeView === 'promotions'
                ? 'bg-[#7A5CFF] text-white'
                : 'border border-[#22304A] bg-[#111A2E] text-[#9CB0C7] hover:bg-[#1A2437]'
            }`}
          >
            <Tag className="w-4 h-4" />
            By Promotion
          </button>
        </div>

        {/* Data Table */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden">
          {activeView === 'skus' ? (
            <>
              <div className="p-6 border-b border-[#22304A]">
                <h3 className="text-lg font-semibold text-white">Conversions by SKU</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#111A2E]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">SKU</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Views</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Conversions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#22304A]">
                    {skuData.map((item) => (
                      <tr key={item.sku} className="hover:bg-[#111A2E]/40">
                        <td className="px-6 py-4">
                          <code className="px-2 py-1 text-xs rounded bg-[#1A2437] text-[#3EF1C7]">{item.sku}</code>
                        </td>
                        <td className="px-6 py-4 text-white">{item.title}</td>
                        <td className="px-6 py-4 text-[#9CB0C7]">{item.views.toLocaleString()}</td>
                        <td className="px-6 py-4 text-white font-medium">{item.conversions}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs rounded bg-[#3EF1C7]/10 text-[#3EF1C7]">{item.rate}%</span>
                        </td>
                        <td className="px-6 py-4 text-[#8CF78E] font-medium">${item.revenue.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <div className={`flex items-center gap-1 ${item.trend >= 0 ? 'text-[#8CF78E]' : 'text-[#F55454]'}`}>
                            {item.trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                            <span className="text-sm">{Math.abs(item.trend)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="p-6 border-b border-[#22304A]">
                <h3 className="text-lg font-semibold text-white">Conversions by Promotion</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#111A2E]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Uses</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Conversions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#22304A]">
                    {promotionData.map((item) => (
                      <tr key={item.code} className="hover:bg-[#111A2E]/40">
                        <td className="px-6 py-4">
                          <code className="px-2 py-1 text-xs rounded bg-[#7A5CFF]/10 text-[#7A5CFF] font-mono">{item.code}</code>
                        </td>
                        <td className="px-6 py-4 text-[#9CB0C7]">{item.type}</td>
                        <td className="px-6 py-4 text-[#9CB0C7]">{item.uses}</td>
                        <td className="px-6 py-4 text-white font-medium">{item.conversions}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs rounded bg-[#8CF78E]/10 text-[#8CF78E]">{item.rate}%</span>
                        </td>
                        <td className="px-6 py-4 text-[#8CF78E] font-medium">${item.revenue.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            item.status === 'active' 
                              ? 'bg-[#3EF1C7]/10 text-[#3EF1C7] border border-[#3EF1C7]/20'
                              : 'bg-[#9CB0C7]/10 text-[#9CB0C7] border border-[#9CB0C7]/20'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
